import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";
import { useDeferUntilInteraction } from "@/hooks/use-defer-interaction";

interface FormEmbedProps {
  id?: string;
  className?: string;
}

/**
 * Embeds the LeadConnector form widget. The iframe (and its form_embed.js
 * helper, which pulls in reCAPTCHA) is only mounted after the first user
 * interaction — see useDeferUntilInteraction. Until then a same-height
 * placeholder holds the space so there is zero layout shift.
 */
const FormEmbed = ({ id = "inline-l0Xup9Zza7GkOhde8oby", className = "" }: FormEmbedProps) => {
  const ready = useDeferUntilInteraction();
  const minHeight = siteConfig.widgets.formHeight;

  useEffect(() => {
    if (!ready) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    }
  }, [ready]);

  return (
    <div className={className} style={{ minHeight }}>
      {ready ? (
        <iframe
          src={siteConfig.widgets.formIframeSrc}
          style={{ width: "100%", minHeight, border: "none", borderRadius: 10, display: "block" }}
          id={id}
          data-layout="{'id':'INLINE'}"
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name={siteConfig.widgets.formName}
          data-height={siteConfig.widgets.formHeight}
          data-layout-iframe-id={id}
          data-form-id={siteConfig.widgets.formId}
          title={siteConfig.widgets.formName}
          scrolling="no"
          loading="lazy"
        />
      ) : (
        <div
          className="flex items-center justify-center text-muted-foreground text-sm"
          style={{ minHeight }}
          aria-hidden="true"
        >
          <span className="inline-flex items-center gap-2">
            <span className="h-4 w-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
            Loading form…
          </span>
        </div>
      )}
    </div>
  );
};

export default FormEmbed;
